import React, { useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PackagesContext } from "../context/PackagesContext";
import { ThemeContext } from "../context/ThemeContext";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { HeartIcon } from "@heroicons/react/24/solid";

const CountryLandingPage = () => {
  const { countryName } = useParams();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const { packages: allPackages, loading } = useContext(PackagesContext);

  const countryPackages = useMemo(() => {
    return allPackages.filter(
      (pkg) => pkg.country?.toLowerCase() === countryName.toLowerCase()
    );
  }, [allPackages, countryName]);

  const displayedPackages = useMemo(() => {
    return countryPackages.map((pkg) => ({
      ...pkg,
      title: pkg.packageName,
      img: pkg.image2,
      price: `₹${pkg.price.toLocaleString()}`,
      duration: pkg.duration || "5 Nights / 6 Days",
      numberOfNights: pkg.numberOfNights || 5,
      numberOfDays: pkg.numberOfDays || 6,
      priceNote: pkg.priceNote || `INR ${pkg.price.toLocaleString()}`,
      destination: pkg.destination || pkg.packageName,
    }));
  }, [countryPackages]);

  // Image URL based on countryName param (lowercase for safety)
  const countryImageUrl = (() => {
    const cn = countryName.toLowerCase();
    if (cn === "uae") {
      return "https://production.tripocationholiday.com/images/d2565bb3-0620-4325-861b-1dfa4399ceae_Dubai.png";
    } else if (cn === "thailand") {
      return "https://production.tripocationholiday.com/images/226cb535-caf4-4525-8f48-aa5b3475125d_Photo One_11zon.png";
    } else if (cn === "hong kong") {
      return "https://production.tripocationholiday.com/images/30a7ee5b-c35a-4d60-b5b0-77e6e968deb9_1.avif";
    } else {
      return "https://production.tripocationholiday.com/images/d7f64208-07b6-400b-ae12-756c3993de3c_Photo Five.webp";
    }
  })();

  const handleTrip = (pkg) => {
    navigate(`/packages-description?destination=${pkg.id}`);
  };

  const smoothScroll = () => {
    const el = document.getElementById("quote-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 py-28 px-4">
      <Helmet>
        <title>{`${countryName} Tour Packages | Tripocation Holiday Pvt. Ltd.`}</title>
        <meta
          name="description"
          content={`Explore amazing tour packages in ${countryName} with Tripocation Holiday Pvt. Ltd..`}
        />
        <meta
          property="og:title"
          content={`${countryName} Tour Packages | Tripocation Holiday Pvt. Ltd.`}
        />
        <meta
          property="og:description"
          content={`Explore amazing tour packages in ${countryName} with Tripocation Holiday Pvt. Ltd..`}
        />
      </Helmet>

      <div className="relative w-full mx-auto mb-8 rounded-lg overflow-hidden shadow-lg">
        {/* Background Banner Image */}
        <img
          src={countryImageUrl}
          alt={`${countryName} banner`}
          className="w-full h-[400px] object-cover"
          loading="lazy"
        />

        {/* Overlay Content with Country Name & Button */}
        <div className="absolute inset-0 z-10 bg-black/40 flex flex-col items-center justify-center text-center text-white px-4 gap-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl md:text-7xl font-bold text-white tracking-tight leading-tight mb-6 capitalize"
          >
            <span className="text-yellow-400 font-extrabold">
              {countryName}
            </span>{" "}
            Tour Packages
          </motion.h1>

          <motion.a
            onClick={smoothScroll}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              damping: 15,
              delay: 0.8,
            }}
            whileHover={{
              scale: 1.05,
              y: -4,
              boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.3)",
            }}
            whileTap={{ scale: 0.98 }}
            className="inline-block bg-yellow-500 text-black px-10 py-3 rounded-2xl 
        transition-all duration-300 focus:outline-none 
        focus:ring-2 focus:ring-yellow-300 focus:ring-offset-4 text-xl font-semibold
        shadow-lg shadow-yellow-400/40 hover:shadow-xl hover:shadow-yellow-400/50 cursor-pointer"
            role="button"
            aria-label="Get Free Quote"
          >
            Get Free Quote
            <motion.span
              className="ml-2 inline-block"
              animate={{ x: [0, 4, 0] }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                ease: "easeInOut",
              }}
            >
              →
            </motion.span>
          </motion.a>
        </div>
      </div>

      {/* ORIGINAL PAGE TITLE */}
      <h1 className="text-3xl font-bold mb-8 text-center">
        {countryName} Tour Packages
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-20 max-w-6xl mx-auto px-4">
        {displayedPackages.map((pkg, index) => (
          <motion.div
            key={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: index * 0.1, duration: 0.3 }}
            className="group relative bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 dark:border-gray-700"
          >
            <div className="absolute top-3 right-3 z-10">
              <button className="p-1.5 bg-white/90 dark:bg-gray-700 rounded-full shadow-xs hover:bg-red-50 dark:hover:bg-red-600/20 transition">
                <HeartIcon className="w-5 h-5 text-gray-500 dark:text-white hover:text-red-500" />
              </button>
            </div>

            <div className="relative overflow-hidden rounded-t-lg">
              <motion.img
                whileHover={{ scale: 1.03 }}
                src={pkg.img}
                alt={pkg.title}
                className="w-full h-40 object-cover"
              />
              <div className="absolute bottom-2 bg-gradient-to-r from-purple-800 to-indigo-600 text-white px-4 py-2 rounded-r-full shadow-md backdrop-blur-sm bg-opacity-90 hover:shadow-indigo-400/40 transition-all duration-300">
                <span className="text-sm font-semibold tracking-wide flex items-center gap-1">
                  {pkg.price}
                </span>
              </div>
            </div>

            <div className="p-4">
              <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1 leading-tight">
                {pkg.title}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-300 mb-3">
                {pkg.duration}
              </p>

              <div className="flex gap-2 items-center">
                {/* <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ backgroundColor: "#007bff" }}
                  className="flex items-center justify-center gap-1 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:shadow-md transition-all"
                  onClick={() => handleTrip(pkg)}
                >
                  Trip Details
                </motion.button> */}

                <motion.button
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={smoothScroll}
                  className="px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-md text-sm font-medium hover:border-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Get Free Quote
                </motion.button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Why Choose Us Section */}
<section className="mt-20 my-20 max-w-6xl mx-auto px-4">
  <motion.h2
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 }}
    className="text-center text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-12"
  >
    Why Choose <span className="text-yellow-500">{countryName}</span> Tour With Us?
  </motion.h2>

  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {[
      {
        title: "Family-Friendly Experiences",
        desc: "We curate packages designed for families, ensuring safety, comfort, and fun for all age groups.",
        icon: "👨‍👩‍👧‍👦"
      },
      {
        title: "Expert Local Guides",
        desc: "Our certified local experts make every tour informative, immersive, and memorable.",
        icon: "🗺️"
      },
      {
        title: "Customizable Itineraries",
        desc: "Tailor your trip with flexibility—add days, destinations, or activities as you like.",
        icon: "🛠️"
      },
      {
        title: "Best Price Guarantee",
        desc: "Get the best deals without compromising on quality. 100% value for your money.",
        icon: "💰"
      },
      {
        title: "24/7 Support",
        desc: "We're with you every step of the way—before, during, and after your trip.",
        icon: "📞"
      },
      {
        title: "Verified Reviews",
        desc: "Thousands of happy travelers vouch for our service and experience.",
        icon: "🌟"
      },
    ].map((card, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-md hover:shadow-lg transition duration-300 group"
      >
        <div className="text-4xl mb-4 text-yellow-500 group-hover:scale-110 transition-transform duration-300">
          {card.icon}
        </div>
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2 group-hover:text-yellow-500 transition-colors">
          {card.title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {card.desc}
        </p>
      </motion.div>
    ))}
  </div>
</section>

<div className="my-20 max-w-6xl mx-auto px-4">
  <motion.h2
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="text-3xl md:text-4xl font-bold text-center mb-10 text-gray-800 dark:text-white"
  >
    Why Travellers Love Tripocation Holiday Pvt. Ltd.
  </motion.h2>

  <div className="grid md:grid-cols-3 gap-8">
    {[
      {
        title: "Expert Itineraries",
        desc: "We tailor each package with deep destination knowledge and insights.",
        icon: "🌍",
      },
      {
        title: "Best Price Guarantee",
        desc: "Get top-notch experiences at the most competitive prices.",
        icon: "💸",
      },
      {
        title: "24/7 Customer Support",
        desc: "Our travel experts are always here to help before, during & after your trip.",
        icon: "📞",
      },
    ].map((item, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.2 }}
        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition-all border border-gray-200 dark:border-gray-700"
      >
        <div className="text-4xl mb-4">{item.icon}</div>
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">{item.title}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
      </motion.div>
    ))}
  </div>
</div>

    </div>
  );
};

export default CountryLandingPage;
